import { useSelector } from 'react-redux';
import {
  BodyLarge,
  ParagraphSmallText,
  TitleMedium,
  TitleSmall,
} from '../../../components/Texts';
import {
  selectAllTokensByNetwork,
  selectAllTradableTokensByNetwork,
  selectTokenByNetworkAndSymbol,
} from '../../../redux/slices/tokenSlice';
import { useGetTokenPriceQuery } from '../../../redux/api/public/tokens';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';
import { formatUSDEnsure2Digits } from '../../../common/currency';
import { formatAddress } from '../../../common/addresses';
import { hapticsImpactLight } from '../../../common/haptic';

const GridItem = styled.div`
  background-color: ${({ theme }) => theme.background.primary};
  border-radius: 16px;
  display: flex;
  width: 297px;
  aspect-ratio: 16/9;
  justify-content: space-between;
  flex-direction: column;
  align-items: center;
  transition: transform 0.3s ease;
  position: relative;
  @media (hover: hover) {
    &:hover {
      transform: translateY(-5px);
    }
  }
  @media ${PHONE_MEDIA_QUERY} {
    width: 180px;
    border-radius: 8px;
  }
  cursor: pointer;
`;

const GridImage = styled.img`
  width: 100%;
  height: 163px;
  object-fit: cover;
  @media ${PHONE_MEDIA_QUERY} {
    height: 90px;
  }
  border-radius: inherit;
`;

const LabelContainer = styled.div`
  position: absolute;
  top: 5px;
  left: 5px;
  display: flex;
  justify-content: flex-start;
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 999px;
  padding: 4px 8px;
  gap: 5px;
`;

const Label = styled.span`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.text.title};
`;

const CardBody = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  @media ${PHONE_MEDIA_QUERY} {
    padding: 12px;
  }
`;

const PriceAndLabelContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-end;
  padding-left: 16px;
  padding-right: 16px;
  padding-bottom: 8px;
  @media ${PHONE_MEDIA_QUERY} {
    padding-left: 12px;
    padding-right: 12px;
  }
`;

const ItemTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TokenName = styled.span`
  font-size: 18px;
  font-weight: 500;
  color: white;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 14px;
  }
`;

const Logo = styled.img`
  width: 32px;
  height: 32px;
  @media ${PHONE_MEDIA_QUERY} {
    width: 20px;
    height: 20px;
  }
`;

const StyledParagraphSmallText = styled(ParagraphSmallText)`
  font-weight: 700;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 12px;
    margin-left: auto;
  }
`;

const StyledParagraphSmallerText = styled(ParagraphSmallText)`
  font-size: 12px;
  font-weight: 300;
  line-height: 8px;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 10px;
    margin-left: auto;
    font-weight: 500;
  }
`;

const LINEAR_GRADIENT_MAPPING = {
  BTC: 'linear-gradient(180deg, #F8931A 0%, #AC5E00 100%)',
  SOL: 'linear-gradient(180deg, #12E4A1 0%, #532991 100%)',
  TSLA: 'linear-gradient(180deg, #F2001D 0%, #6E000D 100%)',
  NVIDIA: 'linear-gradient(180deg, #77B900 0%, #2A4100 100%)',
  BONK: 'linear-gradient(180deg, #FEE207 0%, #BE8200 100%)',
};

const Card = ({ item }) => {
  const { data: tokenPrice } = useGetTokenPriceQuery(item?.address, {
    skip: !item?.address,
  });

  const navigate = useNavigate();

  const theme = useTheme();

  return (
    <GridItem
      style={{ background: LINEAR_GRADIENT_MAPPING[item.symbol] }}
      onClick={() => {
        hapticsImpactLight();
        navigate(`/app/tokens/${item.address}?back=/`);
      }}
    >
      <CardBody>
        <ItemTitleContainer>
          <Logo src={item.logoURI} />
          <p>
            <TokenName>{item.symbol}</TokenName>
          </p>
        </ItemTitleContainer>
      </CardBody>
      <PriceAndLabelContainer>
        <StyledParagraphSmallerText style={{ color: 'white' }}>
          {`1 ${item.symbol} =`}
        </StyledParagraphSmallerText>
        <StyledParagraphSmallText style={{ color: 'white' }}>
          {formatUSDEnsure2Digits(tokenPrice?.buyPrice)}
        </StyledParagraphSmallText>
      </PriceAndLabelContainer>
    </GridItem>
  );
};

const ScrollContainer = styled.div`
  width: 100%;
  overflow-x: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
  -ms-overflow-style: none;
`;

const ScrollInner = styled.div`
  display: flex;
  padding-top: 5px;
  gap: 16px;
  width: fit-content;
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  margin-bottom: 8px;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 8px;
  }
`;

export const StyledTitleSmall = styled(TitleSmall)`
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 14px;
  }
`;

const TrendingTokens = () => {
  const btcToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, 'solana', 'BTC'),
  );
  const solToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, 'solana', 'SOL'),
  );

  const teslaToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, 'solana', 'TSLA'),
  );

  const bonkToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, 'solana', 'BONK'),
  );

  const nvidiaToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, 'solana', 'NVIDIA'),
  );

  const theme = useTheme();

  return (
    <Container>
      <StyledTitleSmall style={{ fontWeight: 400, color: theme.text.title }}>
        Trending Gift Cards
      </StyledTitleSmall>
      <ScrollContainer>
        <ScrollInner>
          {btcToken && <Card item={btcToken} />}
          {solToken && <Card item={solToken} />}
          {teslaToken && <Card item={teslaToken} />}
          {bonkToken && <Card item={bonkToken} />}
          {nvidiaToken && <Card item={nvidiaToken} />}
        </ScrollInner>
      </ScrollContainer>
    </Container>
  );
};

export default TrendingTokens;
